import { useState, useCallback, useEffect, type ReactNode } from "react";
import { useSsrBootstrap } from "@/lib/ssr-bootstrap";
import {
  ImpersonationContext,
  type ImpersonationTarget,
  loadImpersonationFromStorage,
  saveImpersonationToStorage,
  clearImpersonationFromStorage,
  saveImpersonationOrigin,
} from "@/hooks/useImpersonation";
import { useUser } from "@/hooks/useUser";
import { fetchEffectiveHostStatus, isMissingProfileError } from "@/lib/users-api";

const IMPERSONATION_DURATION_MS = 60 * 60 * 1000;

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { isAdmin, isLoading, hasPermission, session, setHostOverride } = useUser();
  const ssrBootstrap = useSsrBootstrap();
  const [impersonating, setImpersonating] = useState<ImpersonationTarget | null>(() => {
    return ssrBootstrap ? null : loadImpersonationFromStorage();
  });

  // Task #6301 — eligibility mirrors the backend gate for the impersonation
  // endpoint: the `users.impersonate` permission, with tier-3 super_admin as
  // an implicit wildcard (that wildcard lives inside `hasPermission`, shared
  // with the rest of the admin shell). The previous hardcoded
  // `admin_role >= 3` check self-cancelled sessions the backend had happily
  // granted to tier-1/2 admins holding the permission.
  const eligible = isAdmin && hasPermission("users.impersonate");

  useEffect(() => {
    if (!impersonating) return;
    // Expiry is checked before eligibility so an expired session is dropped
    // regardless of profile state.
    if (impersonating.expiresAt < Date.now()) {
      clearImpersonationFromStorage();
      setImpersonating(null);
      return;
    }
    // Task #6301 — never clear while the auth profile is still hydrating:
    // on a page refresh `profile` is briefly null (isAdmin false), and
    // clearing here would silently wipe a valid session even for admins who
    // are perfectly eligible. Only clear once the profile has resolved and
    // the user is confirmed ineligible.
    if (isLoading) return;
    if (!eligible) {
      clearImpersonationFromStorage();
      setImpersonating(null);
      return;
    }
    const timeout = setTimeout(() => {
      clearImpersonationFromStorage();
      setImpersonating(null);
    }, impersonating.expiresAt - Date.now());
    return () => clearTimeout(timeout);
  }, [impersonating, isLoading, eligible]);

  // Task #4155 — keep the AuthProvider host-status overlay in sync with the
  // active impersonation session. While impersonating, fetch the impersonated
  // user's effective host status (the impersonation headers are attached
  // automatically) and overlay its `is_host` onto the admin's base profile so
  // the host-gated UI flips on. When not impersonating, clear the override so
  // the admin's own host status is restored immediately. Fail closed (no host
  // UI) if the fetch fails or the token isn't ready yet.
  useEffect(() => {
    if (!impersonating) {
      setHostOverride(null);
      return;
    }
    const token = session?.access_token;
    if (!token) return;
    let cancelled = false;
    fetchEffectiveHostStatus(token)
      .then((res) => {
        if (!cancelled) setHostOverride(!!res.is_host);
      })
      .catch((err) => {
        // Task #5423 — if the impersonated principal has no public.users row
        // yet (authenticated-but-no-profile), this 404s with "User profile
        // not found". Treat it as the known not-a-host state and fail closed
        // (no host UI), same as any other failure.
        if (!cancelled) setHostOverride(false);
        if (isMissingProfileError(err)) return;
      });
    return () => {
      cancelled = true;
    };
  }, [impersonating, session?.access_token, setHostOverride]);

  const startImpersonation = useCallback((target: ImpersonationTarget) => {
    // Task #6273 — record where the admin currently is (path + query) so
    // ending the session can return them there. Captured centrally so every
    // start point (trip detail "Log in as", user 360, etc.) is covered.
    saveImpersonationOrigin(
      window.location.pathname + window.location.search,
    );
    const withExpiry: ImpersonationTarget = {
      ...target,
      expiresAt: target.expiresAt || Date.now() + IMPERSONATION_DURATION_MS,
    };
    saveImpersonationToStorage(withExpiry);
    setImpersonating(withExpiry);
  }, []);

  const stopImpersonation = useCallback(() => {
    clearImpersonationFromStorage();
    setImpersonating(null);
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonating,
        startImpersonation,
        stopImpersonation,
        isImpersonating: impersonating !== null,
        effectiveUserId: impersonating?.userId ?? null,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}
